import React, { useRef } from 'react';
import { Animated, View, StyleSheet, ScrollView } from 'react-native';
import { useSelector } from "react-redux";
import { DefaultRootState } from "@reduxLocal/persistState";
// Removed: import { DataTable } from 'tamagui';
import { useLottieAnim } from '@hooks/lottieHook';
import LottieView from '@utils/lottieAnimation';
import { SubscriptionInfo } from '../modals/RegisterModal/Components/SubscriptionInfo';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
// import { Separator } from 'tamagui'; // Not directly used in the DataTable replacement, keep if needed elsewhere

// Import Material-UI components
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper'; // Used as a container for the table
import Typography from '@mui/material/Typography'; // For text within table cells and general display

const DynamicPlanInfo = ({ selectedPlan, verticalTable }) => {
  const animation = useLottieAnim(); // Used for LottieView ref
  const planIndex = useSelector((state: DefaultRootState) => state.modals.registerModal.selectedPlan);
  const SubInfo = SubscriptionInfo;
  const theme = themeSelector(); // Assuming this returns a boolean (true for light, false for dark)
  const textColor = theme ? "black" : "white"; // Determine text color based on theme

  // Determine which plan data to use: selectedPlan.metadata or a default from SubInfo
  const plan = selectedPlan ? selectedPlan.metadata : SubInfo[planIndex?.plan];

  // Helper for Lottie based on feature inclusion (true/false)
  const renderFeatureLottie = (hasFeature) => {
    return (
      <LottieView
        style={{ flex: 1, minHeight: 30, maxHeight: 50 }} // Consistent animation style
        ref={animation}
        source={hasFeature ? require('../../../assets/animations/featureAnimation2.json') : require('../../../assets/animations/featureAnimation.json')}
        autoPlay
        loop={false}
      />
    );
  };

  return (
    <View style={{ flex: 1, alignSelf: "stretch", width: "100%" }}>
      {/* Top Table: Plan Overview */}
      <TableContainer
        component={Paper} // Use Paper component for Material-UI styling (elevation, background)
        sx={{
          flex: 1,
          borderColor: "rgb(90,192,234)",
          maxHeight: 100, // Fixed height for the overview table
          top: -20,
          border: '1px solid rgb(90,192,234)', // Add a border for the container
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)', // Subtle shadow
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="plan overview table">
          <TableHead>
            <TableRow sx={{ borderBottom: '2px solid rgb(90,192,234)' }}>
              {!selectedPlan && (
                <TableCell sx={{ color: textColor, fontWeight: 'bold' }}>
                  <Typography sx={{ color: textColor, fontWeight: 'bold' }}>Plan</Typography>
                </TableCell>
              )}
              <TableCell sx={{ color: textColor, fontWeight: 'bold' }}>
                <Typography sx={{ color: textColor, fontWeight: 'bold' }}>Data Models</Typography>
              </TableCell>
              <TableCell sx={{ color: textColor, fontWeight: 'bold' }}>
                <Typography sx={{ color: textColor, fontWeight: 'bold' }}>Max Users</Typography>
              </TableCell>
              <TableCell sx={{ color: textColor, fontWeight: 'bold' }}>
                <Typography sx={{ color: textColor, fontWeight: 'bold' }}>Storage Limit</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              {!selectedPlan && (
                <TableCell>
                  <Typography sx={{ color: textColor }}>{plan?.plan}</Typography>
                </TableCell>
              )}
              <TableCell>
                <Typography sx={{ color: textColor }}>
                  {selectedPlan ? (selectedPlan.metadata?.data_limit <= 0 ? "Unlimited" : selectedPlan.metadata?.data_limit) : plan?.data_models}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography sx={{ color: textColor }}>
                  {selectedPlan ? selectedPlan.metadata?.max_users : plan?.users}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography sx={{ color: textColor }}>
                  {selectedPlan ? selectedPlan.metadata?.storage_limit : plan?.storage}GB
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Main Feature Table */}
      <ScrollView style={{ flex: 10, top: 20 }}>
        {verticalTable ? (
          // Vertical Feature Table
          <TableContainer component={Paper} sx={{ flex: 1, marginY: 2, border: '1px solid #e0e0e0', boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)' }}>
            <Table aria-label="vertical features table">
              <TableHead>
                <TableRow sx={{ borderBottom: '2px solid #e0e0e0' }}>
                  <TableCell sx={{ color: textColor, fontWeight: 'bold' }}>
                    <Typography sx={{ color: textColor, fontWeight: 'bold' }}>Feature</Typography>
                  </TableCell>
                  <TableCell sx={{ color: textColor, fontWeight: 'bold', textAlign: 'center' }}>
                    <Typography sx={{ color: textColor, fontWeight: 'bold' }}>Included</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Dynamically render features if `plan.features` is an array of objects or strings */}
                {/* Assuming `plan.features` is an array of booleans corresponding to features */}
                {[
                  { name: 'Basic Support', included: plan?.features[0] },
                  { name: 'Basic ML and AI', included: plan?.features[1] },
                  { name: 'Limited API Integration', included: plan?.features[2] },
                  { name: 'Internal Messaging', included: plan?.features[3] },
                  { name: 'External Messaging', included: plan?.features[4] },
                  { name: 'Business Networking', included: plan?.features[5] },
                  { name: 'Extended API', included: plan?.features[6] },
                  { name: '24/7 Support', included: plan?.features[7] },
                ].map((feature, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      flex: 1,
                      borderBottom: '2px solid rgba(224, 224, 224, 1)', // Adjust border color/width
                    }}
                  >
                    <TableCell>
                      <Typography sx={{ color: textColor }}>{feature.name}</Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {renderFeatureLottie(feature.included)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          // Horizontal Feature Table (single row)
          <TableContainer component={Paper} sx={{ flex: 1, marginY: 2, border: '1px solid #e0e0e0', boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)' }}>
            <Table aria-label="horizontal features table">
              <TableHead>
                <TableRow sx={{ borderBottom: '2px solid #e0e0e0' }}>
                  {/* Assuming these are fixed feature names for horizontal display */}
                  <TableCell sx={{ color: textColor, fontWeight: 'bold' }}>
                    <Typography sx={{ color: textColor, fontWeight: 'bold' }}>Basic Support</Typography>
                  </TableCell>
                  <TableCell sx={{ color: textColor, fontWeight: 'bold' }}>
                    <Typography sx={{ color: textColor, fontWeight: 'bold' }}>Basic ML and AI</Typography>
                  </TableCell>
                  <TableCell sx={{ color: textColor, fontWeight: 'bold' }}>
                    <Typography sx={{ color: textColor, fontWeight: 'bold' }}>Limited API Integration</Typography>
                  </TableCell>
                  <TableCell sx={{ color: textColor, fontWeight: 'bold' }}>
                    <Typography sx={{ color: textColor, fontWeight: 'bold' }}>Internal Messaging</Typography>
                  </TableCell>
                  <TableCell sx={{ color: textColor, fontWeight: 'bold' }}>
                    <Typography sx={{ color: textColor, fontWeight: 'bold' }}>External Messaging</Typography>
                  </TableCell>
                  <TableCell sx={{ color: textColor, fontWeight: 'bold' }}>
                    <Typography sx={{ color: textColor, fontWeight: 'bold' }}>Business Networking</Typography>
                  </TableCell>
                  <TableCell sx={{ color: textColor, fontWeight: 'bold' }}>
                    <Typography sx={{ color: textColor, fontWeight: 'bold' }}>Extended API</Typography>
                  </TableCell>
                  <TableCell sx={{ color: textColor, fontWeight: 'bold' }}>
                    <Typography sx={{ color: textColor, fontWeight: 'bold' }}>24/7 Support</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow sx={{ borderBottom: '1px solid #e0e0e0' }}>
                  {plan?.features?.map((feature, index) => (
                    <TableCell
                      key={index}
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'center',
                        display: 'flex', // Use flex to center content in cell
                        height: '100%', // Ensure it takes full height of row
                      }}
                    >
                      {renderFeatureLottie(feature)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </ScrollView>
    </View>
  );
};

// No longer need StyleSheet.create for table styles, as they are inline with sx prop
// const tableStyleSheet = StyleSheet.create({
//   rowStyle: {
//     flex: 1,
//     borderBottomWidth: 2
//   },
//   animationStyle: {
//     flex: 1,
//     minHeight: 30,
//     maxHeight: 50
//   }
// });

export default DynamicPlanInfo;